/**
 * Uniform circular motion and the centripetal force that sustains it.
 *
 *   a_c = v² / r = ω² r = 4π² r / T²        centripetal acceleration, toward the centre
 *   F_c = m v² / r = m ω² r                 the net inward force that causes it
 *   v = ω r,  ω = 2π f = 2π / T,  f = 1 / T
 *
 * OpenStax University Physics Vol. 1, §4.4 (uniform circular motion) and §6.3 (centripetal
 * force). The centripetal force is not a separate kind of force: it is whatever real force
 * (tension, friction, gravity, the normal force) points toward the centre.
 */

import type {
  RelationDef,
  RelationError,
} from "@/lib/tool-kit/physics-mechanics/relation";
import {
  ANGULAR_SPEED,
  FORCE,
  FREQUENCY,
  LENGTH,
  MASS,
  SPEED,
  TIME,
} from "@/lib/tool-kit/physics-mechanics/units";

const fail = (error: string): RelationError => ({ error });

export type CentripetalSpeedKey = "F" | "m" | "v" | "r";

export const CENTRIPETAL_SPEED: RelationDef<CentripetalSpeedKey> = {
  keys: ["F", "m", "v", "r"],
  symbols: { F: "Fc", m: "m", v: "v", r: "r" },
  families: { F: FORCE, m: MASS, v: SPEED, r: LENGTH },
  equation: "Fc = m × v² ÷ r",
  solvedFor: "F",
  rearranged: {
    F: "Fc = m × v² ÷ r",
    m: "m = Fc × r ÷ v²",
    v: "v = √(Fc × r ÷ m)",
    r: "r = m × v² ÷ Fc",
  },
  solve(unknown, { F, m, v, r }) {
    if (unknown !== "m" && !(m > 0)) {
      return fail("mass-not-positive");
    }
    if (unknown !== "r" && !(r > 0)) {
      return fail("radius-not-positive");
    }
    if (unknown !== "F" && F < 0) {
      return fail("force-negative");
    }
    switch (unknown) {
      case "F":
        return (m * v * v) / r;
      case "m":
        return v === 0 ? fail("speed-zero") : (F * r) / (v * v);
      case "v":
        return Math.sqrt((F * r) / m);
      default:
        return F > 0 ? (m * v * v) / F : fail("force-zero");
    }
  },
};

export type CentripetalAngularKey = "F" | "m" | "w" | "r";

export const CENTRIPETAL_ANGULAR: RelationDef<CentripetalAngularKey> = {
  keys: ["F", "m", "w", "r"],
  symbols: { F: "Fc", m: "m", w: "ω", r: "r" },
  families: { F: FORCE, m: MASS, w: ANGULAR_SPEED, r: LENGTH },
  equation: "Fc = m × ω² × r",
  solvedFor: "F",
  rearranged: {
    F: "Fc = m × ω² × r",
    m: "m = Fc ÷ (ω² × r)",
    w: "ω = √(Fc ÷ (m × r))",
    r: "r = Fc ÷ (m × ω²)",
  },
  solve(unknown, { F, m, w, r }) {
    if (unknown !== "m" && !(m > 0)) {
      return fail("mass-not-positive");
    }
    if (unknown !== "r" && !(r > 0)) {
      return fail("radius-not-positive");
    }
    if (unknown !== "F" && F < 0) {
      return fail("force-negative");
    }
    switch (unknown) {
      case "F":
        return m * w * w * r;
      case "m":
        return w === 0 ? fail("angular-speed-zero") : F / (w * w * r);
      case "w":
        return Math.sqrt(F / (m * r));
      default:
        return w === 0 ? fail("angular-speed-zero") : F / (m * w * w);
    }
  },
};

export type GivenMotion = "v" | "w" | "T" | "f";

export type CircularError = "not-positive" | "radius-needed" | "radius";

export interface CircularMotion {
  /** Angular speed, rad/s. */
  omega: number;
  /** Period, s. */
  period: number;
  /** Frequency, Hz. */
  frequency: number;
  /** Revolutions per minute. */
  rpm: number;
  /** Linear speed, m/s — null without a radius. */
  speed: number | null;
  /** Centripetal acceleration, m/s² — null without a radius. */
  acceleration: number | null;
}

/**
 * Convert between the ways of saying how fast something goes round a circle. `radius`
 * may be null when only ω, T and f are wanted; v and a_c need it.
 */
export function circularMotion(
  given: GivenMotion,
  value: number,
  radius: number | null
): CircularMotion | { error: CircularError } {
  if (!(value > 0)) {
    return { error: "not-positive" };
  }
  if (radius !== null && !(radius > 0)) {
    return { error: "radius" };
  }
  if (given === "v" && radius === null) {
    return { error: "radius-needed" };
  }
  const twoPi = 2 * Math.PI;
  const omegaByGiven = {
    v: radius === null ? 0 : value / radius,
    w: value,
    T: twoPi / value,
    f: twoPi * value,
  };
  const omega = omegaByGiven[given];
  const speed = radius === null ? null : omega * radius;
  return {
    omega,
    period: twoPi / omega,
    frequency: omega / twoPi,
    rpm: (omega / twoPi) * 60,
    speed,
    acceleration: radius === null ? null : omega * omega * radius,
  };
}

/** Units offered for the "given" quantity of the conversion mode. */
export const GIVEN_FAMILIES = {
  v: SPEED,
  w: ANGULAR_SPEED,
  T: TIME,
  f: FREQUENCY,
} as const;
